/* ==========================================================================
   Form toasts - shared by the "Book an appointment" and "Contact us" forms.
   Figma: Turakhia Eyewear, node 5261:9257 ("Form Toasts - Appointment & Contact").

   Pairs with assets/form-toast.css. Loaded by sections/store-detail-form.liquid
   and sections/contact-us.liquid.

   USAGE
     var t = TurakhiaToast.show({
       variant: 'success' | 'progress' | 'error' | 'validation',
       title:  'Appointment requested',
       body:   "We'll call within 24 hours to confirm.",
       action: { label: 'Retry', onClick: function () { ... } },  // optional
       key:    'appointment',   // showing another toast with the same key REPLACES it
       duration: 4000           // optional; omit to use the per-variant default
     });
     t.close();

   Durations follow the design note ("auto-dismiss after ~4s, errors stay until
   closed"): success and validation auto-dismiss; error persists with a close
   button; progress persists until the result toast replaces it by key.

   No dependencies, no theme-core classes extended - this is a page-level overlay,
   not a modal, so ModalElement/DrawerElement don't apply.
   ========================================================================== */

(function () {
  'use strict';

  if (window.TurakhiaToast) return; // both sections load this; only define once

  /* NOTE on innerHTML below: it is only ever assigned these hardcoded icon
     constants. Every caller-supplied string (title, body, action label) goes in
     via textContent, so form input cannot inject markup. A linter will still
     flag the innerHTML lines - this is why they're safe. */

  /* Icons are the exact SVGs exported from the Figma node, inlined so there are
     no extra asset files and no extra requests. Their sizes DIFFER by design -
     each keeps its own exported box inside the 22x22 icon frame, so do not
     collapse these into one shared width/height. */
  var ICONS = {
    success:
      '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M18.3333 5.5L8.25 15.5833L3.66667 11" stroke="#16A34A" stroke-width="2.01667" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>',
    progress:
      '<svg width="18.3333" height="18.3333" viewBox="0 0 18.3333 18.3333" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M9.16667 0.916667C7.53497 0.916667 5.93992 1.40052 4.58322 2.30704C3.22651 3.21356 2.16909 4.50204 1.54466 6.00953C0.920242 7.51702 0.756864 9.17582 1.07519 10.7762C1.39352 12.3765 2.17926 13.8465 3.33304 15.0003C4.48682 16.1541 5.95683 16.9398 7.55718 17.2581C9.15752 17.5765 10.8163 17.4131 12.3238 16.7887C13.8313 16.1643 15.1198 15.1068 16.0263 13.7501C16.9328 12.3934 17.4167 10.7984 17.4167 9.16667" stroke="#178C6F" stroke-width="1.83333" stroke-linecap="round"/>' +
      '<path opacity="0.25" d="M17.4167 9.16667C17.4167 6.97863 16.5475 4.88021 15.0003 3.33304C13.4531 1.78586 11.3547 0.916667 9.16667 0.916667" stroke="#178C6F" stroke-width="1.83333" stroke-linecap="round"/>' +
      '</svg>',
    error:
      '<svg width="18.2417" height="18.2417" viewBox="0 0 18.2417 18.2417" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M9.12083 17.3708C13.6772 17.3708 17.3708 13.6772 17.3708 9.12083C17.3708 4.56448 13.6772 0.870833 9.12083 0.870833C4.56448 0.870833 0.870833 4.56448 0.870833 9.12083C0.870833 13.6772 4.56448 17.3708 9.12083 17.3708Z" stroke="#DB3B3B" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M9.12083 4.99583V9.57917" stroke="#DB3B3B" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M9.12083 12.7875V12.8975" stroke="#DB3B3B" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>',
    validation:
      '<svg width="17.1417" height="15.4" viewBox="0 0 17.1417 15.4" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M8.57083 0.870833L16.2708 14.5292H0.870833L8.57083 0.870833Z" stroke="#D9822B" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M8.57083 6.37083V10.3125" stroke="#D9822B" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M8.57083 13.2458V13.3833" stroke="#D9822B" stroke-width="1.74167" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>'
  };

  var CLOSE_ICON =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '</svg>';

  var DEFAULT_DURATION = {
    success: 4000,
    validation: 4000,
    progress: 0, // persists until replaced by the result toast
    error: 0     // "errors stay until closed"
  };

  var MAX_VISIBLE = 3;

  var container = null;
  var byKey = {}; // key -> toast element

  function getContainer() {
    if (container && document.body.contains(container)) return container;
    container = document.createElement('div');
    container.className = 'tt-toasts';
    container.setAttribute('role', 'region');
    container.setAttribute('aria-label', 'Notifications');
    document.body.appendChild(container);
    return container;
  }

  function remove(el) {
    if (!el || el.dataset.closing === '1') return;
    el.dataset.closing = '1';
    if (el.dataset.key && byKey[el.dataset.key] === el) delete byKey[el.dataset.key];
    if (el._timer) clearTimeout(el._timer);

    el.classList.add('tt-out');
    var done = function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    // animationend may never fire (reduced motion, background tab) - always
    // guarantee removal so a stuck node can't block the container.
    el.addEventListener('animationend', done, { once: true });
    setTimeout(done, 400);
  }

  function show(opts) {
    opts = opts || {};
    var variant = ICONS[opts.variant] ? opts.variant : 'success';
    var duration = typeof opts.duration === 'number' ? opts.duration : DEFAULT_DURATION[variant];
    var root = getContainer();

    var el = document.createElement('div');
    el.className = 'tt-toast tt-toast--' + variant;
    // role=alert is assertive; use it only for errors so routine success
    // messages don't interrupt a screen reader mid-sentence.
    el.setAttribute('role', variant === 'error' ? 'alert' : 'status');

    var chip = document.createElement('div');
    chip.className = 'tt-toast__chip';
    chip.innerHTML = ICONS[variant];
    el.appendChild(chip);

    var text = document.createElement('div');
    text.className = 'tt-toast__text';

    var title = document.createElement('p');
    title.className = 'tt-toast__title';
    title.textContent = opts.title || '';
    text.appendChild(title);

    if (opts.body) {
      var body = document.createElement('p');
      body.className = 'tt-toast__body';
      body.textContent = opts.body;
      text.appendChild(body);
    }
    el.appendChild(text);

    if (opts.action && opts.action.label) {
      var action = document.createElement('button');
      action.type = 'button';
      action.className = 'tt-toast__action';
      action.textContent = opts.action.label;
      action.addEventListener('click', function () {
        remove(el);
        if (typeof opts.action.onClick === 'function') opts.action.onClick();
      });
      el.appendChild(action);
    }

    // Anything that doesn't dismiss itself must be dismissible by hand.
    // Progress is excluded: it is always replaced by a result toast.
    if (duration === 0 && variant !== 'progress') {
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'tt-toast__close';
      close.setAttribute('aria-label', 'Dismiss');
      close.innerHTML = CLOSE_ICON;
      close.addEventListener('click', function () { remove(el); });
      el.appendChild(close);
    }

    // Same key replaces the previous toast outright (progress -> result), so the
    // two never stack.
    if (opts.key) {
      var prev = byKey[opts.key];
      if (prev && prev.parentNode) {
        if (prev._timer) clearTimeout(prev._timer);
        prev.parentNode.removeChild(prev);
      }
      el.dataset.key = opts.key;
      byKey[opts.key] = el;
    }

    root.appendChild(el);

    while (root.children.length > MAX_VISIBLE) {
      remove(root.children[0]);
    }

    if (duration > 0) {
      el._timer = setTimeout(function () { remove(el); }, duration);
      // Don't yank a toast out from under someone reading or using it.
      el.addEventListener('mouseenter', function () {
        if (el._timer) { clearTimeout(el._timer); el._timer = null; }
      });
      el.addEventListener('mouseleave', function () {
        if (!el._timer && el.dataset.closing !== '1') {
          el._timer = setTimeout(function () { remove(el); }, 1500);
        }
      });
    }

    return {
      close: function () { remove(el); },
      element: el
    };
  }

  function dismiss(key) {
    if (byKey[key]) remove(byKey[key]);
  }

  window.TurakhiaToast = { show: show, dismiss: dismiss };
})();
